import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

function FeriencampsBooking() {
    const { user, isMember, isAdmin, authFetch } = useAuth();
    const [events, setEvents] = useState([]);
    const [bundles, setBundles] = useState([]);
    const [allowedFeriencamps, setAllowedFeriencamps] = useState(null); // null = alle erlaubt (nicht-Member), [] = keine, [ids] = bestimmte
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedBundle, setSelectedBundle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        parent_name: '',
        email: '',
        phone: '',
        child_name: '',
        child_age: '',
        message: ''
    });

    useEffect(() => {
        fetchEvents();
        fetchBundles();
        // Wenn Member eingeloggt ist, Freischaltungen laden
        if (isMember && user?.memberId) {
            fetchAllowedFeriencamps();
        }
    }, [isMember, user?.memberId]);

    const fetchAllowedFeriencamps = async () => {
        try {
            const response = await authFetch(`/api/members/${user.memberId}/feriencamp-access`);
            const result = await response.json();
            if (result.data) {
                setAllowedFeriencamps(result.data);
            }
        } catch (err) {
            console.error('Fehler beim Laden der Freischaltungen:', err);
        }
    };

    const fetchEvents = () => {
        setLoading(true);
        fetch('/api/public/feriencamp-events')
            .then(res => res.json())
            .then(data => {
                setEvents(data.data || []);
                setLoading(false);
            })
            .catch(err => {
                console.error('Fehler beim Laden:', err);
                setLoading(false);
            });
    };

    const fetchBundles = () => {
        fetch('/api/public/feriencamp-bundles')
            .then(res => res.json())
            .then(data => setBundles(data.data || []))
            .catch(err => console.error('Fehler beim Laden der Bundles:', err));
    };

    // Filter events based on member access
    const getFilteredEvents = () => {
        // Admins sehen alles
        if (isAdmin) return events;
        // Nicht-eingeloggte User sehen alles (oeffentliche Buchung)
        if (!isMember) return events;
        // Member ohne Freischaltungen sehen nichts
        if (allowedFeriencamps === null) return events; // Noch am Laden
        if (allowedFeriencamps.length === 0) return [];
        // Member sehen nur freigeschaltete (parseInt fuer Type-Safety)
        return events.filter(e => allowedFeriencamps.map(id => parseInt(id)).includes(parseInt(e.id)));
    };

    const filteredEvents = getFilteredEvents();

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        fetch('/api/public/book-feriencamp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                event_id: selectedEvent.id,
                ...formData,
                child_age: formData.child_age ? parseInt(formData.child_age) : null
            })
        })
            .then(res => res.json())
            .then(data => {
                setSubmitting(false);
                if (data.message === 'success') {
                    setSuccess({ ...data.data, event: selectedEvent });
                    setSelectedEvent(null);
                    setFormData({
                        parent_name: '',
                        email: '',
                        phone: '',
                        child_name: '',
                        child_age: '',
                        message: ''
                    });
                    fetchEvents();
                } else {
                    setError(data.error || 'Ein Fehler ist aufgetreten');
                }
            })
            .catch(err => {
                setSubmitting(false);
                setError('Verbindungsfehler. Bitte versuche es erneut.');
            });
    };

    const handleBundleSubmit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        fetch('/api/public/book-feriencamp-bundle', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                bundle_id: selectedBundle.id,
                ...formData,
                child_age: formData.child_age ? parseInt(formData.child_age) : null
            })
        })
            .then(res => res.json())
            .then(data => {
                setSubmitting(false);
                if (data.message === 'success') {
                    setSuccess({ ...data.data, bundle: selectedBundle, isBundle: true });
                    setSelectedBundle(null);
                    setFormData({
                        parent_name: '',
                        email: '',
                        phone: '',
                        child_name: '',
                        child_age: '',
                        message: ''
                    });
                    fetchEvents();
                    fetchBundles();
                } else {
                    setError(data.error || 'Ein Fehler ist aufgetreten');
                }
            })
            .catch(err => {
                setSubmitting(false);
                setError('Verbindungsfehler. Bitte versuche es erneut.');
            });
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('de-DE', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatDateRange = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const startStr = start.toLocaleDateString('de-DE', { day: '2-digit', month: 'long' });
        const endStr = end.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
        return `${startStr} - ${endStr}`;
    };

    const formatPrice = (price) => {
        if (!price) return null;
        return parseFloat(price).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    };

    // Prueft ob Fruehbucher-Preis gilt und gibt die aktuellen Preise zurueck
    const getActivePrices = (event) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const isEarlyBird = event.early_bird_deadline &&
            event.price_early_bird &&
            new Date(event.early_bird_deadline) >= today;

        if (isEarlyBird) {
            return {
                isEarlyBird: true,
                deadline: event.early_bird_deadline,
                price: event.price_early_bird,
                price_sibling: event.price_early_bird_sibling || event.price_sibling,
                price_member: event.price_early_bird_member || event.price_member,
                price_member_sibling: event.price_early_bird_member_sibling || event.price_member_sibling,
                regular_price: event.price // Zum Anzeigen des durchgestrichenen Preises
            };
        }

        return {
            isEarlyBird: false,
            price: event.price,
            price_sibling: event.price_sibling,
            price_member: event.price_member,
            price_member_sibling: event.price_member_sibling
        };
    };

    const formatDeadline = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const calculateDays = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
    };

    // Success view
    if (success) {
        return (
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-md-8 col-lg-6">
                        <div className="card border-success">
                            <div className="card-body text-center py-5">
                                <div className="mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" className="text-success" viewBox="0 0 16 16">
                                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                    </svg>
                                </div>
                                <h2 className="text-success mb-3">Anmeldung erfolgreich!</h2>
                                <p className="lead mb-4">
                                    Vielen Dank, {success.parent_name}!<br />
                                    Deine Anmeldung wurde bestaetigt.
                                </p>
                                <div className="bg-light rounded p-3 mb-4">
                                    <strong>{success.title}</strong><br />
                                    {success.isBundle ? (
                                        (success.events || []).map(e => (
                                            <div key={e.id}>{formatDateRange(e.start_date, e.end_date)} – {e.title}</div>
                                        ))
                                    ) : (
                                        formatDateRange(success.start_date, success.end_date)
                                    )}
                                </div>
                                <p className="text-muted mb-4">
                                    Vielen Dank fuer deine Anmeldung! Wir melden uns zeitnah bei dir.
                                </p>
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => setSuccess(null)}
                                >
                                    Weitere Anmeldung
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    <div className="text-center mb-4">
                        <h1 className="h2">Feriencamps</h1>
                        <p className="lead text-muted">
                            Spannende Ferienprogramme fuer Kinder und Jugendliche!
                        </p>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Laden...</span>
                            </div>
                        </div>
                    ) : selectedEvent ? (
                        /* Booking Form */
                        <div className="row justify-content-center">
                            <div className="col-md-8">
                                <div className="card">
                                    <div className="card-header bg-primary text-white">
                                        <h5 className="mb-0">Anmeldung zum Feriencamp</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="alert alert-light border mb-4">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <h6 className="mb-1">{selectedEvent.title}</h6>
                                                    {selectedEvent.age_group && (
                                                        <span className="badge bg-info mb-2">{selectedEvent.age_group}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {selectedEvent.price && (() => {
                                                const prices = getActivePrices(selectedEvent);
                                                return (
                                                    <>
                                                        {prices.isEarlyBird && (
                                                            <div className="alert alert-warning py-2 mb-2">
                                                                <strong>Fruehbucher-Rabatt!</strong> Gueltig bis {formatDeadline(prices.deadline)}
                                                            </div>
                                                        )}
                                                        <table className="table table-sm table-bordered mt-2 mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th colSpan="2" className="text-center">
                                                                        Preise {prices.isEarlyBird && <span className="badge bg-warning text-dark ms-1">Fruehbucher</span>}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td>Normalpreis</td>
                                                                    <td className="text-end fw-bold">
                                                                        {prices.isEarlyBird && prices.regular_price && (
                                                                            <span className="text-muted text-decoration-line-through me-2" style={{ fontSize: '0.85rem' }}>
                                                                                {formatPrice(prices.regular_price)}
                                                                            </span>
                                                                        )}
                                                                        {formatPrice(prices.price)}
                                                                    </td>
                                                                </tr>
                                                                {prices.price_sibling && (
                                                                    <tr>
                                                                        <td>Geschwisterkind</td>
                                                                        <td className="text-end">{formatPrice(prices.price_sibling)}</td>
                                                                    </tr>
                                                                )}
                                                                {prices.price_member && (
                                                                    <tr>
                                                                        <td>Mitglieder</td>
                                                                        <td className="text-end">{formatPrice(prices.price_member)}</td>
                                                                    </tr>
                                                                )}
                                                                {prices.price_member_sibling && (
                                                                    <tr>
                                                                        <td>Mitglieder Geschwisterkind</td>
                                                                        <td className="text-end">{formatPrice(prices.price_member_sibling)}</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </>
                                                );
                                            })()}
                                            <div className="mt-2">
                                                <strong>{formatDateRange(selectedEvent.start_date, selectedEvent.end_date)}</strong><br />
                                                <span className="text-muted">
                                                    {calculateDays(selectedEvent.start_date, selectedEvent.end_date)} Tage,
                                                    taeglich ab {selectedEvent.time} Uhr
                                                </span>
                                            </div>
                                            {selectedEvent.description && (
                                                <p className="mt-2 mb-0 small text-muted">{selectedEvent.description}</p>
                                            )}
                                        </div>

                                        {error && (
                                            <div className="alert alert-danger">{error}</div>
                                        )}

                                        <form onSubmit={handleSubmit}>
                                            <div className="mb-3">
                                                <label className="form-label">Dein Name (Erziehungsberechtigte/r) *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.parent_name}
                                                    onChange={e => setFormData({ ...formData, parent_name: e.target.value })}
                                                    required
                                                    placeholder="Vor- und Nachname"
                                                />
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">E-Mail *</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        value={formData.email}
                                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                                        required
                                                        placeholder="deine@email.de"
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Telefon</label>
                                                    <input
                                                        type="tel"
                                                        className="form-control"
                                                        value={formData.phone}
                                                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                                        placeholder="Fuer Rueckfragen"
                                                    />
                                                </div>
                                            </div>

                                            <hr className="my-4" />
                                            <h6 className="mb-3">Teilnehmendes Kind</h6>

                                            <div className="row">
                                                <div className="col-md-8 mb-3">
                                                    <label className="form-label">Name des Kindes *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.child_name}
                                                        onChange={e => setFormData({ ...formData, child_name: e.target.value })}
                                                        placeholder="Vor- und Nachname"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Alter *</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData.child_age}
                                                        onChange={e => setFormData({ ...formData, child_age: e.target.value })}
                                                        min="4"
                                                        max="18"
                                                        placeholder="Jahre"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <label className="form-label">Nachricht / Besonderheiten</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    value={formData.message}
                                                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                                                    placeholder="Allergien, besondere Beduerfnisse, oder andere Hinweise?"
                                                />
                                            </div>

                                            <div className="alert alert-info mb-4">
                                                <strong>Gut zu wissen:</strong><br />
                                                Da wir eine gemischte Altersgruppe haben, passe ich die Aufgaben täglich an – so wird niemand über- oder unterfordert. Ihr bekommt jeden Abend einen kurzen Update, was wir gemacht haben und wie ich den nächsten Tag gestalte.
                                            </div>

                                            <div className="d-flex gap-2">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-lg flex-grow-1"
                                                    disabled={submitting}
                                                >
                                                    {submitting ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                            Wird angemeldet...
                                                        </>
                                                    ) : (
                                                        'Verbindlich anmelden'
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-lg"
                                                    onClick={() => setSelectedEvent(null)}
                                                    disabled={submitting}
                                                >
                                                    Zurueck
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : selectedBundle ? (
                        /* Bundle Booking Form */
                        <div className="row justify-content-center">
                            <div className="col-md-8">
                                <div className="card">
                                    <div className="card-header bg-primary text-white">
                                        <h5 className="mb-0">Anmeldung zum Bundle</h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="alert alert-light border mb-4">
                                            <h6 className="mb-2">{selectedBundle.title}</h6>
                                            <ul className="mb-2">
                                                {(selectedBundle.events || []).map(e => (
                                                    <li key={e.id}>{formatDateRange(e.start_date, e.end_date)} – {e.title}</li>
                                                ))}
                                            </ul>
                                            {selectedBundle.price && (() => {
                                                const prices = getActivePrices(selectedBundle);
                                                return (
                                                    <>
                                                        {prices.isEarlyBird && (
                                                            <div className="alert alert-warning py-2 mb-2">
                                                                <strong>Fruehbucher-Rabatt!</strong> Gueltig bis {formatDeadline(prices.deadline)}
                                                            </div>
                                                        )}
                                                        <table className="table table-sm table-bordered mt-2 mb-0">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th colSpan="2" className="text-center">
                                                                        Preise {prices.isEarlyBird && <span className="badge bg-warning text-dark ms-1">Fruehbucher</span>}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td>Normalpreis</td>
                                                                    <td className="text-end fw-bold">
                                                                        {prices.isEarlyBird && prices.regular_price && (
                                                                            <span className="text-muted text-decoration-line-through me-2" style={{ fontSize: '0.85rem' }}>
                                                                                {formatPrice(prices.regular_price)}
                                                                            </span>
                                                                        )}
                                                                        {formatPrice(prices.price)}
                                                                    </td>
                                                                </tr>
                                                                {prices.price_sibling && (
                                                                    <tr>
                                                                        <td>Geschwisterkind</td>
                                                                        <td className="text-end">{formatPrice(prices.price_sibling)}</td>
                                                                    </tr>
                                                                )}
                                                                {prices.price_member && (
                                                                    <tr>
                                                                        <td>Mitglieder</td>
                                                                        <td className="text-end">{formatPrice(prices.price_member)}</td>
                                                                    </tr>
                                                                )}
                                                                {prices.price_member_sibling && (
                                                                    <tr>
                                                                        <td>Mitglieder Geschwisterkind</td>
                                                                        <td className="text-end">{formatPrice(prices.price_member_sibling)}</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </>
                                                );
                                            })()}
                                            {selectedBundle.description && (
                                                <p className="mt-2 mb-0 small text-muted">{selectedBundle.description}</p>
                                            )}
                                        </div>

                                        {error && (
                                            <div className="alert alert-danger">{error}</div>
                                        )}

                                        <form onSubmit={handleBundleSubmit}>
                                            <div className="mb-3">
                                                <label className="form-label">Dein Name (Erziehungsberechtigte/r) *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.parent_name}
                                                    onChange={e => setFormData({ ...formData, parent_name: e.target.value })}
                                                    required
                                                    placeholder="Vor- und Nachname"
                                                />
                                            </div>

                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">E-Mail *</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        value={formData.email}
                                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                                        required
                                                        placeholder="deine@email.de"
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Telefon</label>
                                                    <input
                                                        type="tel"
                                                        className="form-control"
                                                        value={formData.phone}
                                                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                                        placeholder="Fuer Rueckfragen"
                                                    />
                                                </div>
                                            </div>

                                            <hr className="my-4" />
                                            <h6 className="mb-3">Teilnehmendes Kind</h6>

                                            <div className="row">
                                                <div className="col-md-8 mb-3">
                                                    <label className="form-label">Name des Kindes *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={formData.child_name}
                                                        onChange={e => setFormData({ ...formData, child_name: e.target.value })}
                                                        placeholder="Vor- und Nachname"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Alter *</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData.child_age}
                                                        onChange={e => setFormData({ ...formData, child_age: e.target.value })}
                                                        min="4"
                                                        max="18"
                                                        placeholder="Jahre"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <label className="form-label">Nachricht / Besonderheiten</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="3"
                                                    value={formData.message}
                                                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                                                    placeholder="Allergien, besondere Beduerfnisse, oder andere Hinweise?"
                                                />
                                            </div>

                                            <div className="d-flex gap-2">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-lg flex-grow-1"
                                                    disabled={submitting}
                                                >
                                                    {submitting ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                            Wird angemeldet...
                                                        </>
                                                    ) : (
                                                        'Verbindlich anmelden'
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-lg"
                                                    onClick={() => setSelectedBundle(null)}
                                                    disabled={submitting}
                                                >
                                                    Zurueck
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : filteredEvents.length === 0 && bundles.length === 0 ? (
                        /* No events available */
                        <div className="text-center py-5">
                            <div className="alert alert-info">
                                {isMember && allowedFeriencamps?.length === 0 ? (
                                    <>
                                        <h5>Keine Feriencamps freigeschaltet</h5>
                                        <p className="mb-0">
                                            Du hast aktuell keinen Zugang zu Feriencamps. Wende dich an deinen Kursleiter fuer eine Freischaltung.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h5>Aktuell keine Feriencamps verfuegbar</h5>
                                        <p className="mb-0">
                                            Schau bald wieder vorbei oder kontaktiere uns direkt fuer Informationen zu kommenden Camps.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Event + Bundle selection */
                        <>
                            {bundles.length > 0 && (
                                <div className="mb-4">
                                    <h5 className="mb-3">Vorteils-Tickets (mehrere Wochen)</h5>
                                    <div className="row">
                                        {bundles.map(bundle => {
                                            const spotsLeftPerEvent = (bundle.events || []).map(e => e.max_participants - parseInt(e.booked_count));
                                            const spotsLeft = spotsLeftPerEvent.length ? Math.min(...spotsLeftPerEvent) : 0;
                                            const isFull = spotsLeft <= 0;

                                            return (
                                                <div key={`bundle-${bundle.id}`} className="col-md-6 col-lg-4 mb-4">
                                                    <div
                                                        className={`card h-100 shadow-sm border-warning ${isFull ? 'border-secondary' : ''}`}
                                                        style={{ cursor: isFull ? 'default' : 'pointer', opacity: isFull ? 0.85 : 1 }}
                                                        onClick={() => !isFull && setSelectedBundle(bundle)}
                                                    >
                                                        <div className="card-body">
                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                <span className="badge bg-warning text-dark mb-2">Bundle</span>
                                                                {isFull ? (
                                                                    <span className="badge bg-danger">Ausgebucht</span>
                                                                ) : (
                                                                    <span className="badge bg-success">
                                                                        {spotsLeft} {spotsLeft === 1 ? 'Platz' : 'Plaetze'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <h5 className="card-title">{bundle.title}</h5>
                                                            <ul className="card-text small mb-2 ps-3">
                                                                {(bundle.events || []).map(e => (
                                                                    <li key={e.id}>{formatDateRange(e.start_date, e.end_date)} – {e.title}</li>
                                                                ))}
                                                            </ul>
                                                            {bundle.price && (() => {
                                                                const prices = getActivePrices(bundle);
                                                                return (
                                                                    <div className="card-text mb-2">
                                                                        {prices.isEarlyBird && (
                                                                            <div className="mb-1">
                                                                                <span className="badge bg-warning text-dark">
                                                                                    Fruehbucher bis {formatDeadline(prices.deadline)}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        {prices.isEarlyBird && prices.regular_price && (
                                                                            <span className="text-muted text-decoration-line-through me-1" style={{ fontSize: '0.8rem' }}>
                                                                                {formatPrice(prices.regular_price)}
                                                                            </span>
                                                                        )}
                                                                        <span className="badge bg-primary me-1">{formatPrice(prices.price)}</span>
                                                                        {prices.price_member && (
                                                                            <span className="badge bg-success">Mitglieder: {formatPrice(prices.price_member)}</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                            {bundle.description && (
                                                                <p className="card-text small text-muted">
                                                                    {bundle.description.length > 100
                                                                        ? bundle.description.substring(0, 100) + '...'
                                                                        : bundle.description}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="card-footer bg-transparent border-0">
                                                            <button
                                                                className={`btn w-100 ${isFull ? 'btn-secondary' : 'btn-warning'}`}
                                                                disabled={isFull}
                                                            >
                                                                {isFull ? 'Ausgebucht' : 'Bundle anmelden'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {filteredEvents.length > 0 && bundles.length > 0 && (
                                <h5 className="mb-3">Einzelne Wochen</h5>
                            )}
                            <div className="row">
                            {filteredEvents.map(event => {
                                const isFull = parseInt(event.booked_count) >= event.max_participants;
                                const spotsLeft = event.max_participants - parseInt(event.booked_count);

                                return (
                                    <div key={event.id} className="col-md-6 col-lg-4 mb-4">
                                        <div
                                            className={`card h-100 shadow-sm ${isFull ? 'border-secondary' : ''}`}
                                            style={{ cursor: isFull ? 'default' : 'pointer', opacity: isFull ? 0.85 : 1 }}
                                            onClick={() => !isFull && setSelectedEvent(event)}
                                        >
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        {event.age_group && (
                                                            <span className="badge bg-info mb-2">{event.age_group}</span>
                                                        )}
                                                        <h5 className="card-title">{event.title}</h5>
                                                    </div>
                                                    {isFull ? (
                                                        <span className="badge bg-danger">Ausgebucht</span>
                                                    ) : (
                                                        <span className="badge bg-success">
                                                            {spotsLeft} {spotsLeft === 1 ? 'Platz' : 'Plaetze'}
                                                        </span>
                                                    )}
                                                </div>
                                                {isFull && (
                                                    <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>
                                                        Keine freien Plaetze mehr. Du kannst dich gerne auf die Warteliste setzen lassen: <a href="mailto:info@futurhinos.de">info@futurhinos.de</a>
                                                    </div>
                                                )}
                                                <p className="card-text">
                                                    <strong>{formatDateRange(event.start_date, event.end_date)}</strong><br />
                                                    <span className="text-muted">
                                                        {calculateDays(event.start_date, event.end_date)} Tage,
                                                        taeglich ab {event.time} Uhr
                                                    </span>
                                                </p>
                                                {event.price && (() => {
                                                    const prices = getActivePrices(event);
                                                    return (
                                                        <div className="card-text mb-2">
                                                            {prices.isEarlyBird && (
                                                                <div className="mb-1">
                                                                    <span className="badge bg-warning text-dark">
                                                                        Fruehbucher bis {formatDeadline(prices.deadline)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <small className="text-muted d-block mb-1">Preise:</small>
                                                            {prices.isEarlyBird && prices.regular_price && (
                                                                <span className="text-muted text-decoration-line-through me-1" style={{ fontSize: '0.8rem' }}>
                                                                    {formatPrice(prices.regular_price)}
                                                                </span>
                                                            )}
                                                            <span className="badge bg-primary me-1">{formatPrice(prices.price)}</span>
                                                            {prices.price_member && (
                                                                <span className="badge bg-success">Mitglieder: {formatPrice(prices.price_member)}</span>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                                {event.description && (
                                                    <p className="card-text small text-muted">
                                                        {event.description.length > 100
                                                            ? event.description.substring(0, 100) + '...'
                                                            : event.description}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="card-footer bg-transparent border-0">
                                                <button
                                                    className={`btn w-100 ${isFull ? 'btn-secondary' : 'btn-primary'}`}
                                                    disabled={isFull}
                                                >
                                                    {isFull ? 'Ausgebucht' : 'Jetzt anmelden'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default FeriencampsBooking;
